/** Create a line set: operator + name → POST /route_sets/ (a name-only shell).
 *
 * On the POST-create path the funnel creates any nested stops FRESH; existing
 * bus stops are reused only on the PATCH path (matched by id). So the console
 * creates the set as a name-only shell here, then navigates to the detail
 * editor where routes and EXISTING stops are attached via PATCH — the
 * picker-based "stops are shared resources" flow. */
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as routeSetsApi from '@/api/routeSets';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

export function LineSetCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setName('');
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => routeSetsApi.create({ tour_operator: tenant!.id, name: name.trim() }),
    onSuccess: (set) => {
      snackbar.show('Line set created. Add routes and stops below.', 'success');
      qc.invalidateQueries({ queryKey: ['route_sets'] });
      reset();
      onClose();
      navigate(`/line-sets/${set.id}`);
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError) {
        const body = e.body as Record<string, unknown> | undefined;
        const nameErr = body?.name;
        if (Array.isArray(nameErr) && typeof nameErr[0] === 'string') {
          setError(nameErr[0]);
          return;
        }
        setError(e.message);
        return;
      }
      setError('Create failed.');
    },
  });

  const canSubmit = tenant !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New line set</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={setTenant}
            label="Operator"
            placeholder="Select operator"
          />
          <TextField
            label="Name" size="small" fullWidth value={name} required
            onChange={(e) => setName(e.target.value)}
            error={!!error} helperText={error ?? 'Routes and stops are added on the next screen.'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          Create line set
        </Button>
      </DialogActions>
    </Dialog>
  );
}
